/**
 * GraphQL operation documents for the live Application_Form transport (task 19.3).
 *
 * Each exported constant is a single operation string that the
 * `appSyncApplicationFormClient` issues via
 * `generateClient().graphql({ query, variables })`. The field names + variable
 * shapes mirror `infrastructure/graphql/schema.graphql` (the Application Form
 * Builder section, wired in task 17.11) exactly, so the live client returns the
 * same DTO shapes the mock returns and flipping a surface mock→live changes
 * nothing in the hooks/components.
 *
 * AWSJSON fields cross the wire as JSON-encoded *strings* (AppSync scalar
 * semantics): the snapshot on `FormVersion`, `values` on `ApplicationResponse`,
 * `approvalDetails` on `PageApproval`, and the `getJudgeProjection` payload are
 * all `JSON.parse`d on the way out (and stringified on the way in for the
 * `values` / `approvalDetails` inputs). See `appSyncApplicationFormClient.ts`.
 *
 * Validates: application-form-builder Requirements 1.1, 2.1, 6.1, 7.1, 9.1, 14.4
 */

// -----------------------------------------------------------------------------
// Shared selection-set fragments (inlined; the schema declares no fragments).
// -----------------------------------------------------------------------------

/** Every scalar of the GraphQL `FormField` type (incl. the additive builder attributes). */
const FORM_FIELD_FIELDS = `
  id formSchemaId formPageId label fieldCategory bindingTarget fieldType
  required wordLimit characterLimit options helpText
  allowedContentTypes maxFileSizeBytes maxFileCount displayOrder
  immutableOnSubmit thirdPersonRequired applicantTypeVisibility
  visibleDivisionKeys audienceSet
`;

/** The `FormSchema` (Application_Form container) + its ordered fields. */
const FORM_SCHEMA_FIELDS = `
  id formType applicantType competitionYear isActive createdAt updatedAt currentVersionNumber
  fields { ${FORM_FIELD_FIELDS} }
`;

/** A `FormPage` + its ordered fields (Req 1, 16, 21, 22). */
const FORM_PAGE_FIELDS = `
  id formSchemaId title helpText helpTextFormat helpTextHtml autoCollapseHelp
  displayOrder visibleDivisionKeys
  audienceSet phase requiresDirectorApproval
  fields { ${FORM_FIELD_FIELDS} }
`;

/** An immutable `FormVersion`; `snapshot` is AWSJSON (parsed client-side, Req 8). */
const FORM_VERSION_FIELDS = `
  id formSchemaId versionNumber competitionId snapshot createdAt
`;

/**
 * An `ApplicationResponse`; `values` is AWSJSON (parsed client-side, Req 6, 7).
 * The trailing `manualLockOverride`/`effectiveLock`/`isPhase1Open`/
 * `isPhase2Unlocked` fields are the season-phase-deadlines additions (Req 16,
 * 17): the Director-set override plus the server-computed derived lock/phase
 * state the client surfaces mirror. `returnFeedbackNote` is the
 * contestant-visible director return note (application-decision-feedback Req
 * 2.4); the director-only `internalRejectNote` is deliberately NOT selectable
 * here (R5 — it is not on the owner-selectable type).
 */
const APPLICATION_RESPONSE_FIELDS = `
  applicantPersonId competitionId formSchemaId formVersionId submissionStatus
  values titlePreferences divisionKey approvedTitle submittedAt createdAt updatedAt
  manualLockOverride effectiveLock isPhase1Open isPhase2Unlocked
  reviewReturn returnFeedbackNote
`;

/**
 * A `DocumentCatalogEntry` (Req 20). Carries the additive upload-constraint
 * fields and the optional `documentType` mapping (apply-document-uploads Req 5,
 * Decision D2); a null constraint is not enforced.
 */
const DOCUMENT_CATALOG_ENTRY_FIELDS = `
  competitionId id label requiredForSubmission visibleDivisionKeys applicantTypeScope
  allowedContentTypes maxFileSizeBytes maxFileCount documentType
  helpText helpTextFormat helpTextHtml phase displayOrder
`;

/**
 * A `DocumentReference` metadata row for one S3-stored uploaded file. Carries
 * the additive, nullable `catalogEntryId` that links the upload to its
 * Document_Catalog_Entry (apply-document-uploads Req 2.3, 7.1, 12.4).
 */
const DOCUMENT_REFERENCE_FIELDS = `
  id applicantId competitionYear documentType s3Key fileName contentType uploadedAt catalogEntryId
`;

/** A `PresignedUrlResponse` — the short-lived S3 PUT URL for a slot upload (Req 2.1). */
const PRESIGNED_URL_FIELDS = `
  uploadUrl s3Key expiresIn
`;

/** A `PageApproval`; `approvalDetails` is nullable AWSJSON (parsed client-side, Req 21). */
const PAGE_APPROVAL_FIELDS = `
  applicantPersonId competitionId formPageId status approvalDetails
`;

/** Judge-visible Approval_Details key config for an approval-required page (Req 22.7). */
const APPROVAL_DETAIL_VISIBILITY_FIELDS = `
  competitionId formPageId judgeVisibleKeys
`;

/** Every scalar of the season-scoped `Title` catalog type (season-scoped-title-registry Req 1, 5.1). */
const TITLE_FIELDS = `
  id competitionYear key label active displayOrder applicantType divisionKey createdAt updatedAt
`;

// -----------------------------------------------------------------------------
// Queries
// -----------------------------------------------------------------------------

// First-login routing destination from Person + Participation state (Req 15).
export const GET_ONBOARDING_DESTINATION = /* GraphQL */ `
  query GetOnboardingDestination {
    getOnboardingDestination {
      kind
      competitionId
    }
  }
`;

// The Application_Form (FormSchema container) for a competition (Req 1.5).
export const GET_APPLICATION_FORM = /* GraphQL */ `
  query GetApplicationForm($competitionId: ID!) {
    getApplicationForm(competitionId: $competitionId) { ${FORM_SCHEMA_FIELDS} }
  }
`;

// The ordered pages of an Application_Form (the working/draft structure, Req 1.5).
// Keyed by formSchemaId so the builder can load its pages even before any
// Form_Version has been published (unlike getActiveFormVersion).
export const LIST_FORM_PAGES = /* GraphQL */ `
  query ListFormPages($formSchemaId: ID!) {
    listFormPages(formSchemaId: $formSchemaId) { ${FORM_PAGE_FIELDS} }
  }
`;

// The active immutable Form_Version a response is captured against (Req 5.1, 8).
// The schema query is keyed by competitionId only; division scoping is applied
// server-side / by the renderer.
export const GET_ACTIVE_FORM_VERSION = /* GraphQL */ `
  query GetActiveFormVersion($competitionId: ID!) {
    getActiveFormVersion(competitionId: $competitionId) { ${FORM_VERSION_FIELDS} }
  }
`;

// The caller's own Application_Response for a competition, or null (Req 6.2).
export const GET_MY_APPLICATION_RESPONSE = /* GraphQL */ `
  query GetMyApplicationResponse($competitionId: ID!) {
    getMyApplicationResponse(competitionId: $competitionId) { ${APPLICATION_RESPONSE_FIELDS} }
  }
`;

// Director review: all responses for a competition (Req 9, 12).
export const LIST_APPLICATION_RESPONSES = /* GraphQL */ `
  query ListApplicationResponses($competitionId: ID!) {
    listApplicationResponses(competitionId: $competitionId) { ${APPLICATION_RESPONSE_FIELDS} applicantName }
  }
`;

// One response for director review, rendered through its captured version
// client-side from the returned ApplicationResponse (Req 9.1, 9.3, 9.4).
export const GET_APPLICATION_RESPONSE_FOR_REVIEW = /* GraphQL */ `
  query GetApplicationResponseForReview($competitionId: ID!, $applicantPersonId: ID!) {
    getApplicationResponseForReview(competitionId: $competitionId, applicantPersonId: $applicantPersonId) {
      ${APPLICATION_RESPONSE_FIELDS}
    }
  }
`;

// Director review decision on a SUBMITTED application (applicant-application-flow):
// APPROVE / REJECT / REQUEST_CHANGES. Writes the ApplicationResponse SSOT the
// applicant's /apply surface reads.
export const REVIEW_APPLICATION_RESPONSE = /* GraphQL */ `
  mutation ReviewApplicationResponse($competitionId: ID!, $applicantPersonId: ID!, $decision: String!, $note: String) {
    reviewApplicationResponse(competitionId: $competitionId, applicantPersonId: $applicantPersonId, decision: $decision, note: $note) {
      applicantPersonId competitionId submissionStatus updatedAt
    }
  }
`;

// Director review render (Req 9.1, 9.3): the captured response projected
// server-side through its recorded Form_Version into the review envelope
// { applicantPersonId, competitionId, submissionStatus, isDraft, sections },
// returned as AWSJSON (parsed client-side into a RenderedResponse). This is the
// live counterpart of the mock's client-side render.
export const GET_RENDERED_RESPONSE_FOR_REVIEW = /* GraphQL */ `
  query GetRenderedResponseForReview($competitionId: ID!, $applicantPersonId: ID!) {
    getRenderedResponseForReview(competitionId: $competitionId, applicantPersonId: $applicantPersonId)
  }
`;

// Per-competition document catalog (Req 20).
export const GET_DOCUMENT_CATALOG = /* GraphQL */ `
  query GetDocumentCatalog($competitionId: ID!) {
    getDocumentCatalog(competitionId: $competitionId) { ${DOCUMENT_CATALOG_ENTRY_FIELDS} }
  }
`;

// The applicant's uploaded Document_References for a competition year
// (apply-document-uploads Req 7.1). The live client derives `applicantId` from
// the authenticated caller — never from surface state.
export const LIST_DOCUMENT_REFERENCES = /* GraphQL */ `
  query ListDocumentReferences($applicantId: ID!, $competitionYear: Int!) {
    listDocumentReferences(applicantId: $applicantId, competitionYear: $competitionYear) {
      ${DOCUMENT_REFERENCE_FIELDS}
    }
  }
`;

// Per-applicant page approvals for a competition (Req 21).
export const LIST_PAGE_APPROVALS = /* GraphQL */ `
  query ListPageApprovals($competitionId: ID!, $applicantPersonId: ID!) {
    listPageApprovals(competitionId: $competitionId, applicantPersonId: $applicantPersonId) {
      ${PAGE_APPROVAL_FIELDS}
    }
  }
`;

// Every contestant's PageApproval for ONE approval page, for the read-only
// Approvals roll-up status column (approval-rollup-surfaces Req 3.1). Backed by
// the competitionPage-applicantPersonId-index GSI (PK `${competitionId}#${formPageId}`),
// so records for a different competition or page are excluded by construction.
// Director-gated, read-only. The backend query + resolver land in task 3.3.
export const LIST_PAGE_APPROVALS_FOR_PAGE = /* GraphQL */ `
  query ListPageApprovalsForPage($competitionId: ID!, $formPageId: ID!) {
    listPageApprovalsForPage(competitionId: $competitionId, formPageId: $formPageId) {
      ${PAGE_APPROVAL_FIELDS}
    }
  }
`;

// The AUTHENTICATED caller's OWN page approvals for a competition (Req 21).
// Strictly self-scoped: no applicantPersonId argument — the server resolves the
// applicant from the caller's Cognito identity, so a contestant can only ever
// read their own approvals. Powers the /apply renderer's page lock + Approved pill.
export const GET_MY_PAGE_APPROVALS = /* GraphQL */ `
  query GetMyPageApprovals($competitionId: ID!) {
    getMyPageApprovals(competitionId: $competitionId) {
      ${PAGE_APPROVAL_FIELDS}
    }
  }
`;

// Judge-audience projection as ApplicationSection[] — AWSJSON (Req 21.8, 22.4).
export const GET_JUDGE_PROJECTION = /* GraphQL */ `
  query GetJudgeProjection($competitionId: ID!, $applicantPersonId: ID!) {
    getJudgeProjection(competitionId: $competitionId, applicantPersonId: $applicantPersonId)
  }
`;

// Director-configured judge-visible Approval_Details keys per approval-required
// page for a competition (Req 22.7).
export const GET_APPROVAL_DETAIL_VISIBILITY = /* GraphQL */ `
  query GetApprovalDetailVisibility($competitionId: ID!) {
    getApprovalDetailVisibility(competitionId: $competitionId) { ${APPROVAL_DETAIL_VISIBILITY_FIELDS} }
  }
`;

// The active, per-competitionYear season Title_Catalog, ordered by displayOrder
// (season-scoped-title-registry Req 5.1). Authenticated-readable so the
// contestant apply UI and the director review approved-title control can list
// the selectable titles through the application-form client seam.
export const LIST_TITLES = /* GraphQL */ `
  query ListTitles($competitionYear: Int!) {
    listTitles(competitionYear: $competitionYear) { ${TITLE_FIELDS} }
  }
`;

// -----------------------------------------------------------------------------
// Mutations
// -----------------------------------------------------------------------------

// Contact intake creates/completes the global Person SSOT (Req 14.4).
export const SUBMIT_CONTACT_INTAKE = /* GraphQL */ `
  mutation SubmitContactIntake($input: ContactIntakeInput!) {
    submitContactIntake(input: $input) {
      personId
    }
  }
`;

// Page CRUD + reorder (director/admin; Req 1).
export const CREATE_FORM_PAGE = /* GraphQL */ `
  mutation CreateFormPage($input: CreateFormPageInput!) {
    createFormPage(input: $input) { ${FORM_PAGE_FIELDS} }
  }
`;

export const UPDATE_FORM_PAGE = /* GraphQL */ `
  mutation UpdateFormPage($input: UpdateFormPageInput!) {
    updateFormPage(input: $input) { ${FORM_PAGE_FIELDS} }
  }
`;

export const REORDER_FORM_PAGES = /* GraphQL */ `
  mutation ReorderFormPages($formSchemaId: ID!, $orderedIds: [ID!]!) {
    reorderFormPages(formSchemaId: $formSchemaId, orderedIds: $orderedIds) { ${FORM_PAGE_FIELDS} }
  }
`;

export const DELETE_FORM_PAGE = /* GraphQL */ `
  mutation DeleteFormPage($id: ID!) {
    deleteFormPage(id: $id)
  }
`;

// Field create/update reuse the existing form-schema mutations; reorder is the
// new application-form mutation (Req 2, 3, 17).
export const CREATE_FORM_FIELD = /* GraphQL */ `
  mutation CreateFormField($input: CreateFormFieldInput!) {
    createFormField(input: $input) { ${FORM_FIELD_FIELDS} }
  }
`;

export const UPDATE_FORM_FIELD = /* GraphQL */ `
  mutation UpdateFormField($input: UpdateFormFieldInput!) {
    updateFormField(input: $input) { ${FORM_FIELD_FIELDS} }
  }
`;

export const REORDER_FORM_FIELDS = /* GraphQL */ `
  mutation ReorderFormFields($formPageId: ID!, $orderedIds: [ID!]!) {
    reorderFormFields(formPageId: $formPageId, orderedIds: $orderedIds) { ${FORM_FIELD_FIELDS} }
  }
`;

// Field delete reuses the existing form-schema `deleteFormField` mutation (Req 2).
export const DELETE_FORM_FIELD = /* GraphQL */ `
  mutation DeleteFormField($id: ID!) {
    deleteFormField(id: $id)
  }
`;

// Snapshot an immutable Form_Version (Req 8).
export const PUBLISH_FORM_VERSION = /* GraphQL */ `
  mutation PublishFormVersion($formSchemaId: ID!) {
    publishFormVersion(formSchemaId: $formSchemaId) { ${FORM_VERSION_FIELDS} }
  }
`;

// Response draft/submit (owner; Req 6, 7). `values` is sent as an AWSJSON string.
export const SAVE_APPLICATION_RESPONSE_DRAFT = /* GraphQL */ `
  mutation SaveApplicationResponseDraft($input: SaveDraftInput!) {
    saveApplicationResponseDraft(input: $input) { ${APPLICATION_RESPONSE_FIELDS} }
  }
`;

export const SUBMIT_APPLICATION_RESPONSE = /* GraphQL */ `
  mutation SubmitApplicationResponse($input: SubmitInput!) {
    submitApplicationResponse(input: $input) { ${APPLICATION_RESPONSE_FIELDS} }
  }
`;

// Bound-field write routing to canonical entities (Req 17, 18, 19).
export const SELECT_DIVISION = /* GraphQL */ `
  mutation SelectDivision($competitionId: ID!, $divisionKey: String!) {
    selectDivision(competitionId: $competitionId, divisionKey: $divisionKey) {
      participationId
      personId
      competitionYear
      divisionKey
    }
  }
`;

export const SELECT_SEASON = /* GraphQL */ `
  mutation SelectSeason($competitionId: ID!, $competitionYear: Int!) {
    selectSeason(competitionId: $competitionId, competitionYear: $competitionYear) {
      participationId
      personId
      competitionYear
      divisionKey
    }
  }
`;

export const SAVE_TITLE_PREFERENCES = /* GraphQL */ `
  mutation SaveTitlePreferences($competitionId: ID!, $ranked: [String!]!) {
    saveTitlePreferences(competitionId: $competitionId, ranked: $ranked) {
      ${APPLICATION_RESPONSE_FIELDS}
    }
  }
`;

// Director/admin: the title SSOT on Participation (Req 19.5, 12.6).
export const SET_APPROVED_TITLE = /* GraphQL */ `
  mutation SetApprovedTitle($competitionId: ID!, $applicantPersonId: ID!, $title: String!) {
    setApprovedTitle(competitionId: $competitionId, applicantPersonId: $applicantPersonId, title: $title) {
      participationId
      personId
      approvedTitle
    }
  }
`;

// Document catalog upsert/delete (director/admin; Req 20).
export const UPSERT_DOCUMENT_CATALOG_ENTRY = /* GraphQL */ `
  mutation UpsertDocumentCatalogEntry($input: DocumentCatalogEntryInput!) {
    upsertDocumentCatalogEntry(input: $input) { ${DOCUMENT_CATALOG_ENTRY_FIELDS} }
  }
`;

export const DELETE_DOCUMENT_CATALOG_ENTRY = /* GraphQL */ `
  mutation DeleteDocumentCatalogEntry($competitionId: ID!, $id: ID!) {
    deleteDocumentCatalogEntry(competitionId: $competitionId, id: $id)
  }
`;

// Persist a new document-catalog order within a competition; returns the
// reordered entries (application-builder-documents Req 4.2). Mirrors the
// reorderFormFields shape.
export const REORDER_DOCUMENT_CATALOG_ENTRIES = /* GraphQL */ `
  mutation ReorderDocumentCatalogEntries($competitionId: ID!, $orderedIds: [ID!]!) {
    reorderDocumentCatalogEntries(competitionId: $competitionId, orderedIds: $orderedIds) { ${DOCUMENT_CATALOG_ENTRY_FIELDS} }
  }
`;

// Document upload path (owner; apply-document-uploads Req 2, 6). These reuse the
// shared presign + DocumentReference backend the Document Center uses; the live
// client derives `applicantId` from the authenticated caller, never an argument.

// Presigned S3 PUT URL for a slot upload (Req 2.1).
export const GET_DOCUMENT_UPLOAD_URL = /* GraphQL */ `
  mutation GetDocumentUploadUrl($input: GetUploadUrlInput!) {
    getDocumentUploadUrl(input: $input) { ${PRESIGNED_URL_FIELDS} }
  }
`;

// Persist a DocumentReference after a successful S3 PUT, linked to its slot by
// `catalogEntryId` (Req 2.3, 2.5).
export const CREATE_DOCUMENT_REFERENCE = /* GraphQL */ `
  mutation CreateDocumentReference($input: CreateDocumentReferenceInput!) {
    createDocumentReference(input: $input) { ${DOCUMENT_REFERENCE_FIELDS} }
  }
`;

// Remove an uploaded document (Req 6.1).
export const DELETE_DOCUMENT_REFERENCE = /* GraphQL */ `
  mutation DeleteDocumentReference($id: ID!) {
    deleteDocumentReference(id: $id)
  }
`;

// Per-page approval state machine (director/admin; Req 21). `approvalDetails` is
// sent as an AWSJSON string.
export const SET_PAGE_APPROVAL_STATUS = /* GraphQL */ `
  mutation SetPageApprovalStatus($input: SetPageApprovalInput!) {
    setPageApprovalStatus(input: $input) { ${PAGE_APPROVAL_FIELDS} }
  }
`;

// Configure which Approval_Details keys project into the Merit_Judge_View for an
// approval-required page (director/admin; Req 22.7).
export const SET_APPROVAL_DETAIL_VISIBILITY = /* GraphQL */ `
  mutation SetApprovalDetailVisibility($input: ApprovalDetailVisibilityInput!) {
    setApprovalDetailVisibility(input: $input) { ${APPROVAL_DETAIL_VISIBILITY_FIELDS} }
  }
`;

// season-phase-deadlines (Req 16.6, 18.5, 18.6): set/clear the Director manual
// lock override on a SINGLE Application, keyed by (competitionId,
// applicantPersonId). A null `override` clears it (returns the Application to
// date-governed behavior). Director/admin only. Returns the updated
// ApplicationResponse with the echoed override + server-computed derived fields.
export const SET_APPLICATION_LOCK_OVERRIDE = /* GraphQL */ `
  mutation SetApplicationLockOverride($competitionId: ID!, $applicantPersonId: ID!, $override: ManualLockOverride) {
    setApplicationLockOverride(competitionId: $competitionId, applicantPersonId: $applicantPersonId, override: $override) {
      ${APPLICATION_RESPONSE_FIELDS}
    }
  }
`;

// season-phase-deadlines (Req 18.3, 18.4): bulk set/clear the Director manual
// lock override on EVERY Application in a competition's listed scope. A null
// `override` clears the override on each. Director/admin only. Returns the
// updated ApplicationResponses.
export const SET_APPLICATION_LOCK_OVERRIDES_FOR_COMPETITION = /* GraphQL */ `
  mutation SetApplicationLockOverridesForCompetition($competitionId: ID!, $override: ManualLockOverride) {
    setApplicationLockOverridesForCompetition(competitionId: $competitionId, override: $override) {
      ${APPLICATION_RESPONSE_FIELDS}
    }
  }
`;

/** Every exported operation document, for a future schema-vs-ops guardrail (task 19.4). */
export const ALL_APPLICATION_FORM_OPERATIONS: Record<string, string> = {
  GET_ONBOARDING_DESTINATION,
  GET_APPLICATION_FORM,
  LIST_FORM_PAGES,
  GET_ACTIVE_FORM_VERSION,
  GET_MY_APPLICATION_RESPONSE,
  LIST_APPLICATION_RESPONSES,
  GET_APPLICATION_RESPONSE_FOR_REVIEW,
  GET_RENDERED_RESPONSE_FOR_REVIEW,
  REVIEW_APPLICATION_RESPONSE,
  GET_DOCUMENT_CATALOG,
  LIST_DOCUMENT_REFERENCES,
  LIST_PAGE_APPROVALS,
  LIST_PAGE_APPROVALS_FOR_PAGE,
  GET_MY_PAGE_APPROVALS,
  GET_JUDGE_PROJECTION,
  GET_APPROVAL_DETAIL_VISIBILITY,
  LIST_TITLES,
  SUBMIT_CONTACT_INTAKE,
  CREATE_FORM_PAGE,
  UPDATE_FORM_PAGE,
  REORDER_FORM_PAGES,
  DELETE_FORM_PAGE,
  CREATE_FORM_FIELD,
  UPDATE_FORM_FIELD,
  REORDER_FORM_FIELDS,
  DELETE_FORM_FIELD,
  PUBLISH_FORM_VERSION,
  SAVE_APPLICATION_RESPONSE_DRAFT,
  SUBMIT_APPLICATION_RESPONSE,
  SELECT_DIVISION,
  SAVE_TITLE_PREFERENCES,
  SET_APPROVED_TITLE,
  UPSERT_DOCUMENT_CATALOG_ENTRY,
  DELETE_DOCUMENT_CATALOG_ENTRY,
  REORDER_DOCUMENT_CATALOG_ENTRIES,
  GET_DOCUMENT_UPLOAD_URL,
  CREATE_DOCUMENT_REFERENCE,
  DELETE_DOCUMENT_REFERENCE,
  SET_PAGE_APPROVAL_STATUS,
  SET_APPROVAL_DETAIL_VISIBILITY,
  SET_APPLICATION_LOCK_OVERRIDE,
  SET_APPLICATION_LOCK_OVERRIDES_FOR_COMPETITION,
};
